import { api } from '~/components/Providers/APIClient';
import {
    fullEditUserFormInput,
    fullEditUserFormSchema,
    userFormInput,
} from '~/util/forms/user';
import { ApiMutationOptions } from '~/util/hooks/useApiMutation';
import { Badge } from '~/util/types/badges';
import { User } from '~/util/types/users';

export const createUserMutation = (): ApiMutationOptions<
    User,
    Pick<userFormInput, 'global_name'>
> => ({
    buildRequest: ({ global_name }) => {
        return {
            path: '/users/placeholders',
            method: 'POST',
            attachAuth: true,
            body: {
                username: global_name,
            },
        };
    },
    invalidateQueries: ['users'],
    toastMessages: {
        loading: 'Creating user...',
        error: 'Failed to create user: ',
        success: 'User created successfully',
    },
});

export type updateUserMutationType = {
    data: fullEditUserFormInput;
    current: fullEditUserFormInput;
    uuid: string;
    userid: string;
};
export const updateUserMutation = (): ApiMutationOptions<
    void,
    updateUserMutationType
> => ({
    customMutationFn: async ({ data, uuid, current }) => {
        const { roles, ban_level, ...user } =
            fullEditUserFormSchema.parse(data);

        const {
            roles: prevRoles,
            ban_level: prevBanLevel,
            ...prevUser
        } = fullEditUserFormSchema.parse(current);

        if (JSON.stringify(user) !== JSON.stringify(prevUser)) {
            const userResponse = await api.send<User>({
                path: `/users/${uuid}`,
                method: 'PATCH',
                attachAuth: true,
                body: user,
            });

            if (userResponse.error) {
                throw userResponse;
            }
        }

        if (ban_level !== prevBanLevel) {
            const banLevelResponse = await api.send<void>({
                path: `/users/${uuid}/ban`,
                method: 'PATCH',
                attachAuth: true,
                body: { ban_level },
            });
            if (banLevelResponse.error) {
                throw banLevelResponse;
            }
        }

        const rolesToAdd = roles.filter(
            (role) => !prevRoles.find((r) => r.id === role.id),
        );
        const rolesToRemove = prevRoles.filter(
            (role) => !roles.find((r) => r.id === role.id),
        );

        if (rolesToAdd.length > 0) {
            const responses = await Promise.all(
                rolesToAdd.map((role) =>
                    api.send<void>({
                        path: `/roles/${role.id}/users`,
                        method: 'PATCH',
                        attachAuth: true,
                        body: [uuid],
                    }),
                ),
            );
            for (const response of responses) {
                if (response.error) {
                    throw response;
                }
            }
        }

        if (rolesToRemove.length > 0) {
            const responses = await Promise.all(
                rolesToRemove.map((role) =>
                    api.send<void>({
                        path: `/roles/${role.id}/users`,
                        method: 'DELETE',
                        attachAuth: true,
                        body: [uuid],
                    }),
                ),
            );
            for (const response of responses) {
                if (response.error) {
                    throw response;
                }
            }
        }

        return {
            data: undefined,
            error: false,
            status: 200,
            networkError: false,
        };
    },
    invalidateQueries: ({ variables: { userid } }) => [
        ['users', 'profile', userid],
        ['roles'],
    ],
    toastMessages: {
        loading: 'Updating user...',
        error: 'Failed to update user: ',
        success: 'User updated successfully',
    },
});

export type BadgeMutationOptions = {
    userid: string;
    badgeCode: string;
};

export const grantBadgeMutation = (): ApiMutationOptions<
    Badge[],
    BadgeMutationOptions
> => ({
    buildRequest: ({ userid, badgeCode }) => ({
        path: `/users/${userid}/badges`,
        method: 'PATCH',
        body: {
            badge_code: badgeCode,
        },
        attachAuth: true,
    }),
    toastMessages: {
        loading: 'Granting badge...',
        error: 'Failed to grant badge: ',
        success: () => `Granted badge successfully.`,
    },
    invalidateQueries: ({ variables: { userid } }) => [
        'users',
        'profile',
        userid,
    ],
});

export const revokeBadgeMutation = (): ApiMutationOptions<
    Badge[],
    BadgeMutationOptions
> => ({
    buildRequest: ({ userid, badgeCode }) => ({
        path: `/users/${userid}/badges`,
        method: 'DELETE',
        body: [badgeCode],
        attachAuth: true,
    }),
    toastMessages: {
        loading: 'Revoking badge...',
        error: 'Failed to revoke badge: ',
        success: () => `Revoked badge successfully.`,
    },
    invalidateQueries: ({ variables: { userid } }) => [
        'users',
        'profile',
        userid,
    ],
});
